import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { liveQuery } from 'dexie';
import { environment } from '../../environments/environment';
import { onboardingDb } from '../db/onboarding-db';
import type { BulkJobProgress, BulkJobRecord } from '../models/bulk-job-progress.model';

const POLL_INTERVAL_MS = 3000;

interface StatusResponse {
  data: {
    status: string;
    records?: BulkJobRecord[];
    result?: BulkJobProgress['result'];
    error?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class BulkJobProgressService {
  private readonly pollIntervals = new Map<string, ReturnType<typeof setInterval>>();

  constructor(private http: HttpClient) {}

  /**
   * Start background polling for a bulk job. Updates Dexie on each response.
   * Stops automatically when status is completed or failed.
   */
  startTracking(jobId: string): void {
    if (this.pollIntervals.has(jobId)) return;

    const now = Date.now();
    onboardingDb.bulkJobProgress.put({
      jobId,
      status: 'pending',
      records: [],
      createdAt: now,
      updatedAt: now,
    });

    const poll = () => {
      this.http
        .get<StatusResponse>(`${environment.url}/uploads/bulk-patient/status/${jobId}`, {
          withCredentials: true,
        })
        .subscribe({
          next: (res) => {
            const status = (res?.data?.status ?? 'pending') as BulkJobProgress['status'];
            const records = res?.data?.records ?? [];
            const result = res?.data?.result;
            const error = res?.data?.error;

            onboardingDb.bulkJobProgress.update(jobId, {
              status,
              records,
              ...(result && { result }),
              ...(error && { error }),
              updatedAt: Date.now(),
            });

            const shouldStop = status === 'failed' || status === 'completed';
            if (shouldStop) {
              this.stopTracking(jobId);
            }
          },
          error: () => {
            this.stopTracking(jobId);
          },
        });
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    this.pollIntervals.set(jobId, intervalId);
  }

  stopTracking(jobId: string): void {
    const id = this.pollIntervals.get(jobId);
    if (id) {
      clearInterval(id);
      this.pollIntervals.delete(jobId);
    }
  }

  watchAll(): Observable<BulkJobProgress[]> {
    return new Observable((subscriber) => {
      const sub = liveQuery(() => onboardingDb.bulkJobProgress.orderBy('updatedAt').reverse().toArray()).subscribe(
        (jobs) => subscriber.next(jobs)
      );
      return () => sub.unsubscribe();
    });
  }

  async clearJob(jobId: string): Promise<void> {
    this.stopTracking(jobId);
    await onboardingDb.bulkJobProgress.delete(jobId);
  }

  async clearCompletedAndFailed(): Promise<void> {
    const jobs = await onboardingDb.bulkJobProgress.toArray();
    for (const job of jobs) {
      if (job.status === 'completed' || job.status === 'failed') {
        this.stopTracking(job.jobId);
        await onboardingDb.bulkJobProgress.delete(job.jobId);
      }
    }
  }
}
